// ============================================================================
// CrewAhead Maritime Platform - API Barrel Export
// ============================================================================

// Centralized API configuration (single source of truth)
export {
  API_BASE_URL,
  API_VERSION,
  API_BASE_PATH,
  getBaseUrl,
  getFullUrl,
  getApiPath,
  getAuthHeader,
  getApiHeaders,
  API_URLS,
} from '../api-config';

// Client & utilities
export { apiClient, setTokens, clearAuth, CrewAheadApiError, handleApiError } from './client';
export type { CrewAheadApiError as CrewAheadApiErrorType } from './client';

// Auth API
export {
  login,
  register,
  logout,
  refreshToken,
  verifyEmail,
  resendVerification,
  forgotPassword,
  resetPassword,
  changePassword,
  getCurrentUser,
  updateAccountInfo,
} from './auth';

// Accounts admin API
export {
  adminListUsers,
  adminGetUser,
  adminUpdateUser,
  adminSuspendUser,
  adminReactivateUser,
  adminChangeUserRole,
  adminGetUserAuditLog,
} from './accounts';
export type {
  AdminListUsersParams,
  AdminUpdateUserData,
  ChangeRoleData,
  ChangeRoleResponse,
  StatusResponse,
} from './accounts';

// Profile API
export {
  getProfile,
  updateProfile,
  uploadDocument,
  deleteDocument,
  getDocuments,
  getSeaService,
  createSeaService,
  updateSeaService,
  deleteSeaService,
  getCertifications,
  createCertification,
  updateCertification,
  deleteCertification,
  uploadCertificationWithFile,
} from './profile';

// Jobs API
export {
  getJobs,
  getJob,
  applyToJob,
  getApplications,
  withdrawApplication,
  getSavedJobs,
  saveJob,
  unsaveJob,
  isJobSaved,
  createJobAlert,
  getJobAlerts,
  updateJobAlert,
  deleteJobAlert,
  toggleJobAlert,
  createJobPost,
  updateJobPost,
  closeJobPost,
  reopenJobPost,
  deleteJobPost,
  getJobApplications,
  updateApplicationStatus,
} from './jobs';

// Availability API
export {
  listAvailabilityPosts,
  getAvailabilityPost,
  createAvailabilityPost,
  updateAvailabilityPost,
  deleteAvailabilityPost,
  publishAvailabilityPost,
  contactAvailabilityPost,
  getMyAvailabilityPosts,
  getAvailabilityFilterOptions,
} from './availability';
export type {
  CreateAvailabilityPostData,
  UpdateAvailabilityPostData,
  ContactAvailabilityPostData,
} from './availability';

// Matching API
export {
  getMatchedJobs,
  getMatchScore,
  getMatchedCandidates,
  triggerMatchRecalculate,
} from './matching';

// Institute API
export {
  getMyInstituteProfile,
  getOfferings,
  getOffering,
  createOffering,
  updateOffering,
  deleteOffering,
  getBatches,
  getBatch,
  createBatch,
  updateBatch,
  deleteBatch,
  getPublicOfferings,
  getPublicOfferingDetail,
  getPublicBatches,
  createEnquiry,
  getResources,
  getResource,
  createResource,
  updateResource,
  deleteResource,
  getPublicResources,
  getPublicResourceDetail,
  getResourceEnquiries,
  getResourceEnquiryDetail,
  updateResourceEnquiryStatus,
  createResourceEnquiry,
} from './institute';
export type {
  InstituteCourseOffering,
  InstituteCourseOfferingInput,
  CourseBatch,
  CourseBatchInput,
  PublicInstituteCourseOffering,
  PublicCourseBatch,
  CourseEnquiry,
  CourseEnquiryInput,
  InstituteResource,
  InstituteResourceInput,
  PublicInstituteResource,
  ResourceEnquiry,
  ResourceEnquiryInput,
} from './institute';

// Career Intelligence API
export {
  getCareerTransitionProfile,
  createCareerTransitionProfile,
  updateCareerTransitionProfile,
  updateCareerTargetPositions,
  activateTransitionProfile,
  deactivateTransitionProfile,
  getCareerSummary,
  getReadinessScore,
  getCareerPath,
  getSkillGaps,
  getRecommendedCourses,
  getShoreJobMatches,
  getCareerBarriers,
  recalculateRecommendations,
  getSeaToShorePaths,
  getShoreToSeaPaths,
  getTargetRoles,
  getRoleMatrix,
  generateCareerPath,
} from './careerIntelligence';

// Notifications API
export {
  getNotifications,
  getNotification,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
  getUnreadNotificationCount,
  getUnreadNotifications,
  clearAllNotifications,
  getNotificationPreferences,
  updateNotificationPreferences,
  adminCreateNotification,
  adminBulkCreateNotifications,
} from './notifications';

// Messages API
export {
  getConversations,
  getMessages,
  sendMessage,
  getMessagesByApplication,
  sendMessageByApplication,
} from './messages';
export type {
  Conversation,
  Message,
} from '../types/models';

// Employer API
export {
  getMyEmployerProfile,
  updateMyEmployerProfile,
  getCompanyProfiles,
  createCompanyProfile,
  getCompanyDocuments,
  uploadCompanyDocument,
  getEmployerDashboard,
  getEmployerSubscription,
} from './employer';

// Onboarding API
export {
  getOnboardingSteps,
  getOnboardingStep,
  getOnboardingProgress,
  submitOnboardingStep,
  startOnboarding,
  skipOnboardingStep,
  getOnboardingStatus,
} from './onboarding';

// Candidates API
export {
  getCandidates,
  getAnonymizedCandidates,
  getCandidate,
  getFeaturedCandidates,
  getShortlistedCandidates,
  getCandidateFilters,
  saveCandidate,
  unsaveCandidate,
  contactCandidate,
  revealCandidate,
  getRevealStatus,
} from './candidates';
export type { RevealGateStatus } from './candidates';

// Content API
export {
  getBlogPosts,
  getBlogPost,
  getBlogCategories,
  getBlogTags,
  getBlogSources,
  getKnowledgeHub,
  getRelatedPosts,
  getFAQs,
  getStaticPages,
  getStaticPage,
  getTestimonials,
  getCompanySettings,
  submitContactEnquiry,
  subscribeNewsletter,
} from './content';

// Vessel API
export {
  lookupVesselByIMO,
  lookupVesselByMMSI,
  searchVesselsByName,
  getVessel,
  getPSCReports,
  getPSCReport,
  getVesselSearchHistory,
  getVesselStats,
} from './vessel';

// Payments API
export {
  getSubscriptionPlans,
  getSubscriptionPlansByType,
  getSubscriptions,
  createSubscription,
  cancelSubscription,
  getMySubscriptions,
  getSubscriptionStats,
  getPayments,
  createPayment,
  initiatePayment,
  verifyPayment,
  refundPayment,
  getMyPayments,
  getPaymentDashboardStats,
  getInvoices,
  downloadInvoice,
  getWallet,
  getWalletTransactions,
} from './payments';

// Files API
export {
  getDocuments as getFileDocuments,
  createDocument as createFileDocument,
  getDocument as getFileDocument,
  deleteDocument as deleteFileDocument,
  uploadDocument as uploadFileDocument,
  getPresignedUploadUrl,
} from './files';

// Seafarer API
export {
  getMySeafarerProfile,
  updateMySeafarerProfile,
  getSeafarerProfiles,
  getSeafarerProfile,
  getSeaServiceRecords,
  createSeaServiceRecord,
  getSeaServiceRecord,
  updateSeaServiceRecord,
  deleteSeaServiceRecord,
  getSeafarerCertifications,
  createSeafarerCertification,
  getSeafarerCertification,
  updateSeafarerCertification,
  deleteSeafarerCertification,
  getSeafarerDocuments,
  createSeafarerDocument,
  getSeafarerDocument,
  deleteSeafarerDocument,
  downloadSeafarerDocument,
  searchSeafarers,
  getSeafarerDashboard,
} from './seafarer';

// Shore API
export {
  getMyShoreProfile,
  updateMyShoreProfile,
  getShoreProfiles,
  getShoreProfile,
  getShoreWorkExperience,
  createWorkExperience,
  getWorkExperience,
  updateWorkExperience,
  deleteWorkExperience,
  getShoreDocuments,
  createShoreDocument,
  getShoreDocument,
  deleteShoreDocument,
  getJobPreferences,
  getJobPreference,
  createJobPreference,
  updateJobPreference,
  searchShoreProfiles,
  getShoreDashboard,
} from './shore';

// Note: Wildcard re-exports removed to prevent duplicate export collisions.
// Each module above has explicit named exports. Import directly from the
// specific module when needed: import { getDocuments } from '@/lib/api/files';
